import {
  ASReqOptions,
  getAutosuggestResults,
  getRecommendations,
} from "client/api/autosuggest-service";
import {
  GlobalContext,
  GlobalContextProvider,
} from "client/context/GlobalContext";
import { useEventBus } from "client/hooks/useEventBus";
import { useKeyboardToClose } from "client/hooks/useKeyboardToClose";
import { useTrackingDataLayer } from "client/hooks/useTrackingDataLayer";
import { isValidASTerm } from "client/utils/helpers";
import {
  getCachedRecommendations,
  setCachedRecommendations,
} from "client/utils/storage";
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ASResponse, SearchResponse } from "types/searchApi/response";

const isLocal = process.env.NODE_ENV === "development";

const AutosuggestModal = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "client/components/modal/autosuggest-modal"
    ),
);

const Autosuggest: React.FC<{
  brand: string;
  isSFLVeryIEHeaderEnabled: boolean;
}> = ({ brand, isSFLVeryIEHeaderEnabled }) => {
  const [showModal, setShowModal] = useState(false);
  const [initData, setInitData] = useState<SearchResponse | ASResponse | null>(
    null,
  );

  const openModal = useCallback((): void => {
    setShowModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setShowModal(false);
  }, []);

  const getResults = useCallback<GlobalContext["getResults"]>(
    async (searchTerm?: string, opts?: ASReqOptions) => {
      const reqOpts = { ...opts, userId: window.cnstrcUserId };
      if (!isValidASTerm(searchTerm)) return getRecommendations(reqOpts);
      return getAutosuggestResults(searchTerm, reqOpts);
    },
    [],
  );

  useEventBus("search-bar/open-popup", openModal);
  useEventBus("search-bar/close-popup", closeModal);

  useEffect(() => {
    (async () => {
      const cachedRex = getCachedRecommendations();
      if (cachedRex) return setInitData(cachedRex);

      const res = await getRecommendations({ userId: window.cnstrcUserId });
      if (res) setCachedRecommendations(res);

      setInitData(res);
    })();
  }, []);

  useKeyboardToClose(closeModal);

  useTrackingDataLayer();

  const globalCtxVal = useMemo(
    () => ({
      closeModal,
      getResults,
      initData,
      brand,
      isSFLVeryIEHeaderEnabled,
    }),
    [closeModal, getResults, initData, brand, isSFLVeryIEHeaderEnabled],
  );

  return (
    <>
      <GlobalContextProvider value={globalCtxVal}>
        {showModal && (
          <Suspense fallback={null}>
            <AutosuggestModal />
          </Suspense>
        )}
      </GlobalContextProvider>
      {isLocal && (
        <>
          <meta name="csrf-token" content="123"></meta>
          <button onClick={openModal}>Open Search</button>
        </>
      )}
    </>
  );
};

export default Autosuggest;
